import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { GetNotificationsResponse } from './useGetNotifications';
import type { NegotiationFeedResponse } from '../types/invoice/negotiation';

const includesNegotiationHandoff = (type?: string, excludeType?: string): boolean => {
    if (type && type !== 'negotiation-handoff') return false;
    if (!excludeType) return true;
    return !excludeType.split(',').map((s) => s.trim()).includes('negotiation-handoff');
};

const markAllNotificationsRead = async (sessionId: string | null, type?: string, excludeType?: string) => {
    const queryParams = type ? `?type=${type}` : excludeType ? `?excludeType=${excludeType}` : '';
    await axios.patch(`${API_URL}/notifications/read-all${queryParams}`, {}, {
        headers: {
            Authorization: sessionId
        }
    });
    if (includesNegotiationHandoff(type, excludeType) && sessionId) {
        await axios.post(
            `${API_URL}/invoice/negotiation/read-all`,
            {},
            { headers: { Authorization: sessionId } },
        );
    }
};

export const useMarkAllNotificationsRead = (onOptimisticUpdate?: () => void, type?: string, excludeType?: string) => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    const syncNegotiations = includesNegotiationHandoff(type, excludeType);

    return useMutation({
        mutationFn: () => markAllNotificationsRead(sessionContext?.sessionId ?? null, type, excludeType),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: ['/notifications'] });
            if (syncNegotiations) {
                await queryClient.cancelQueries({ queryKey: ['/invoice/negotiations/unread-count'] });
                await queryClient.cancelQueries({ queryKey: ['/invoice/negotiations/feed'] });
            }

            const previousNotifications = queryClient.getQueriesData<GetNotificationsResponse>({
                queryKey: ['/notifications'],
            });
            const previousUnreadCount = syncNegotiations
                ? queryClient.getQueryData<{ count: number }>(['/invoice/negotiations/unread-count'])
                : undefined;
            const previousFeed = syncNegotiations
                ? queryClient.getQueriesData<{ pages: NegotiationFeedResponse[] }>({
                      queryKey: ['/invoice/negotiations/feed'],
                  })
                : [];

            queryClient.setQueriesData<GetNotificationsResponse>(
                { queryKey: ['/notifications'] },
                (old) => {
                    if (!old) return old;
                    return {
                        ...old,
                        notifications: old.notifications.map(notification => ({
                            ...notification,
                            read: true
                        })),
                        unreadCount: 0
                    };
                }
            );

            if (syncNegotiations) {
                queryClient.setQueryData<{ count: number }>(
                    ['/invoice/negotiations/unread-count'],
                    (old) => (old ? { count: 0 } : old),
                );
                queryClient.setQueriesData<{ pages: NegotiationFeedResponse[] }>(
                    { queryKey: ['/invoice/negotiations/feed'] },
                    (old) => {
                        if (!old?.pages) return old;
                        return {
                            ...old,
                            pages: old.pages.map((page) => ({
                                ...page,
                                negotiations: page.negotiations.map((row) =>
                                    row.unread ? { ...row, unread: false } : row,
                                ),
                            })),
                        };
                    },
                );
            }

            onOptimisticUpdate?.();

            return { previousNotifications, previousUnreadCount, previousFeed, syncNegotiations };
        },
        onError: (_err, _vars, context) => {
            if (!context) return;
            for (const [key, data] of context.previousNotifications) {
                queryClient.setQueryData(key, data);
            }
            if (context.syncNegotiations) {
                queryClient.setQueryData(
                    ['/invoice/negotiations/unread-count'],
                    context.previousUnreadCount,
                );
                for (const [key, data] of context.previousFeed) {
                    queryClient.setQueryData(key, data);
                }
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['/notifications'] });
            if (syncNegotiations) {
                void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/feed'] });
                void queryClient.invalidateQueries({ queryKey: ['/invoice/negotiations/unread-count'] });
            }
        },
    });
};
